import { useState, useMemo } from "react";
import { Calendar } from "@/components/ui/calendar";
import { es } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  toYMD,
  parseYMD,
  formatLongDate,
  formatTime,
  generateTimeSlots,
  generateWeekly,
  generateMonthlyNth,
  DOW_LABELS_LONG,
} from "@/lib/dateUtils";
import { X, Repeat, Clock } from "lucide-react";

/**
 * CalendarBuilder
 * Props:
 *  - selectedDates: string[] (YYYY-MM-DD)
 *  - onDatesChange: (dates) => void
 *  - slots: [{date, start_time, duration}]
 *  - onSlotsChange: (slots) => void
 *  - timeFormat: "12h" | "24h"
 */
export function CalendarBuilder({ selectedDates, onDatesChange, slots, onSlotsChange, timeFormat }) {
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");
  const [recurMode, setRecurMode] = useState("weekly"); // weekly | monthly
  const [weeklyDow, setWeeklyDow] = useState(new Set([2])); // Tuesday default
  const [monthNth, setMonthNth] = useState("3");
  const [monthDow, setMonthDow] = useState("4"); // Thursday

  const [start, setStart] = useState("18:00");
  const [end, setEnd] = useState("20:00");
  const [duration, setDuration] = useState(15);

  const dateObjs = useMemo(() => selectedDates.map(parseYMD), [selectedDates]);

  const handleDayClick = (day) => {
    if (!day) return;
    const ymd = toYMD(day);
    const set = new Set(selectedDates);
    if (set.has(ymd)) set.delete(ymd);
    else set.add(ymd);
    onDatesChange(Array.from(set).sort());
  };

  const applyRecurrence = () => {
    if (!rangeStart || !rangeEnd) return toast.error("Elige el rango de fechas");
    let dates = [];
    if (recurMode === "weekly") {
      if (weeklyDow.size === 0) return toast.error("Elige al menos un día de la semana");
      dates = generateWeekly(rangeStart, rangeEnd, Array.from(weeklyDow));
    } else {
      dates = generateMonthlyNth(rangeStart, rangeEnd, parseInt(monthNth, 10), parseInt(monthDow, 10));
    }
    if (dates.length === 0) return toast.error("No se generó ninguna fecha con esas reglas");
    const merged = Array.from(new Set([...selectedDates, ...dates])).sort();
    onDatesChange(merged);
    toast.success(`Se agregaron ${dates.length} fecha(s)`);
  };

  const removeDate = (ymd) => {
    onDatesChange(selectedDates.filter((d) => d !== ymd));
    onSlotsChange(slots.filter((s) => s.date !== ymd));
  };

  const generateSlotsForAllDays = () => {
    const times = generateTimeSlots(start, end, parseInt(duration, 10));
    if (times.length === 0) return toast.error("El rango de horas es inválido");
    if (selectedDates.length === 0) return toast.error("Primero elige al menos un día");
    const next = [];
    for (const d of selectedDates) {
      for (const t of times) {
        next.push({ date: d, start_time: t, duration: parseInt(duration, 10) });
      }
    }
    onSlotsChange(next);
    toast.success(`Se generaron ${next.length} slots`);
  };

  const toggleWeekly = (d) => {
    const s = new Set(weeklyDow);
    if (s.has(d)) s.delete(d);
    else s.add(d);
    setWeeklyDow(s);
  };

  const slotsByDate = useMemo(() => {
    const map = new Map();
    for (const s of slots) {
      if (!map.has(s.date)) map.set(s.date, []);
      map.get(s.date).push(s);
    }
    for (const arr of map.values()) arr.sort((a, b) => a.start_time.localeCompare(b.start_time));
    return map;
  }, [slots]);

  return (
    <div className="space-y-8">
      {/* Calendar picker */}
      <div className="bg-white border border-line rounded-2xl p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-ink-500 mb-3 block">
              Elige los días
            </Label>
            <Calendar
              mode="multiple"
              selected={dateObjs}
              onDayClick={handleDayClick}
              className="rounded-xl border border-line p-3"
              locale={es}
              data-testid="calendar-picker"
            />
          </div>

          <div className="space-y-5">
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-ink-500 mb-3 block flex items-center gap-2">
                <Repeat size={14} strokeWidth={1.75} /> Recurrencia
              </Label>
              <div className="flex gap-2 mb-4">
                <Button
                  type="button"
                  variant={recurMode === "weekly" ? "default" : "outline"}
                  className={`rounded-full ${recurMode === "weekly" ? "bg-coral hover:bg-coral-hover" : "border-line"}`}
                  onClick={() => setRecurMode("weekly")}
                  data-testid="recur-weekly-btn"
                >
                  Semanal
                </Button>
                <Button
                  type="button"
                  variant={recurMode === "monthly" ? "default" : "outline"}
                  className={`rounded-full ${recurMode === "monthly" ? "bg-coral hover:bg-coral-hover" : "border-line"}`}
                  onClick={() => setRecurMode("monthly")}
                  data-testid="recur-monthly-btn"
                >
                  Mensual
                </Button>
              </div>

              {recurMode === "weekly" && (
                <div>
                  <p className="text-xs text-ink-500 mb-2">Días de la semana</p>
                  <div className="flex flex-wrap gap-2">
                    {DOW_LABELS_LONG.map((label, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => toggleWeekly(i)}
                        className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                          weeklyDow.has(i)
                            ? "bg-coral text-white border-coral"
                            : "bg-white text-ink-700 border-line hover:bg-cream-100"
                        }`}
                        data-testid={`recur-dow-${i}`}
                      >
                        {label.slice(0, 3)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {recurMode === "monthly" && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[11px] text-ink-500">Ocurrencia</Label>
                    <Select value={monthNth} onValueChange={setMonthNth}>
                      <SelectTrigger className="rounded-xl border-line h-10 mt-1" data-testid="recur-nth-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">Primer</SelectItem>
                        <SelectItem value="2">Segundo</SelectItem>
                        <SelectItem value="3">Tercer</SelectItem>
                        <SelectItem value="4">Cuarto</SelectItem>
                        <SelectItem value="5">Último</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[11px] text-ink-500">Día</Label>
                    <Select value={monthDow} onValueChange={setMonthDow}>
                      <SelectTrigger className="rounded-xl border-line h-10 mt-1" data-testid="recur-day-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DOW_LABELS_LONG.map((l, i) => (
                          <SelectItem key={i} value={String(i)}>{l}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] text-ink-500">Desde</Label>
                <Input
                  type="date"
                  value={rangeStart}
                  onChange={(e) => setRangeStart(e.target.value)}
                  className="rounded-xl border-line h-10 mt-1"
                  data-testid="recur-range-start"
                />
              </div>
              <div>
                <Label className="text-[11px] text-ink-500">Hasta</Label>
                <Input
                  type="date"
                  value={rangeEnd}
                  onChange={(e) => setRangeEnd(e.target.value)}
                  className="rounded-xl border-line h-10 mt-1"
                  data-testid="recur-range-end"
                />
              </div>
            </div>

            <Button
              type="button"
              onClick={applyRecurrence}
              className="rounded-full bg-ink-900 hover:bg-ink-700 text-white w-full"
              data-testid="recur-apply-btn"
            >
              Agregar fechas al calendario
            </Button>
          </div>
        </div>
      </div>

      {/* Selected dates list */}
      {selectedDates.length > 0 && (
        <div className="bg-white border border-line rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-xl text-ink-900">
              {selectedDates.length} día{selectedDates.length !== 1 ? "s" : ""} seleccionado{selectedDates.length !== 1 ? "s" : ""}
            </h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {selectedDates.map((d) => (
              <span
                key={d}
                className="inline-flex items-center gap-1.5 bg-cream-100 border border-line rounded-full pl-3 pr-2 py-1 text-sm text-ink-700"
                data-testid={`selected-date-${d}`}
              >
                {formatLongDate(d)}
                <button
                  type="button"
                  onClick={() => removeDate(d)}
                  className="text-ink-300 hover:text-coral"
                  aria-label={`Quitar ${d}`}
                >
                  <X size={14} strokeWidth={1.75} />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Time slot generator */}
      <div className="bg-white border border-line rounded-2xl p-6">
        <Label className="text-xs uppercase tracking-[0.2em] text-ink-500 mb-4 flex items-center gap-2">
          <Clock size={14} strokeWidth={1.75} /> Horarios (se aplican a todos los días seleccionados)
        </Label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-[11px] text-ink-500">Desde</Label>
            <Input
              type="time"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              className="rounded-xl border-line h-10 mt-1"
              data-testid="slot-start-input"
            />
          </div>
          <div>
            <Label className="text-[11px] text-ink-500">Hasta</Label>
            <Input
              type="time"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              className="rounded-xl border-line h-10 mt-1"
              data-testid="slot-end-input"
            />
          </div>
          <div>
            <Label className="text-[11px] text-ink-500">Duración</Label>
            <Select value={String(duration)} onValueChange={(v) => setDuration(v)}>
              <SelectTrigger className="rounded-xl border-line h-10 mt-1" data-testid="slot-duration-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[10, 15, 20, 30, 45, 60, 90, 120].map((v) => (
                  <SelectItem key={v} value={String(v)}>{v} min</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button
          type="button"
          onClick={generateSlotsForAllDays}
          className="mt-5 rounded-full bg-coral hover:bg-coral-hover text-white"
          data-testid="generate-slots-btn"
        >
          Generar horarios
        </Button>

        {slots.length > 0 && (
          <div className="mt-6 space-y-4">
            <p className="text-sm text-ink-500">
              <b className="text-ink-900">{slots.length}</b> slots generados en {slotsByDate.size} día(s)
            </p>
            <div className="space-y-3 max-h-72 overflow-y-auto pr-2">
              {Array.from(slotsByDate.entries()).map(([date, list]) => (
                <div key={date} className="border border-line rounded-xl p-3 bg-cream-50">
                  <div className="text-sm font-medium text-ink-900 mb-2">{formatLongDate(date)}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {list.map((s, i) => (
                      <span key={i} className="text-xs bg-white border border-line rounded-full px-2 py-0.5 text-ink-700">
                        {formatTime(s.start_time, timeFormat)}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
